import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Loading } from '@makyoapp/makyo-ui'

// Channel name for OAuth communication
const OAUTH_CHANNEL = 'makyo-oauth-channel'

export default function PluginOauth2Callback() {
  const [searchParams] = useSearchParams()

  // Define the expected payload type
  type Oauth2TokenPayload = {
    org_id?: string
    plugin_id?: string
    access_token?: string
    refresh_token?: string
    expires_in?: number | string
    [key: string]: any
  }

  const fetchGoogleUserInfo = async (accessToken: string) => {
    try {
      // Fetch user info from Google OAuth2 v2 userinfo endpoint
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user info')
      }

      const data = await response.json()

      return {
        name: data.name || '', // This endpoint should return full name
        email: data.email || '',
      }
    } catch (error) {
      console.error('Error fetching Google user info:', error)
      throw error
    }
  }

  useEffect(() => {
    const handleCallback = async () => {
      const channel = new BroadcastChannel(OAUTH_CHANNEL)

      // Check for error query parameter first
      const errorParam = searchParams.get('error')

      if (errorParam) {
        console.error('OAuth error received:', errorParam)
        // Send error message through channel
        channel.postMessage({
          status: 'error',
          message: 'Authentication failed, please try again',
          error: errorParam,
        })
        channel.close()
        window.close()
        return
      }

      const tokenParam = searchParams.get('token')
      let payload: Oauth2TokenPayload = {}
      if (tokenParam) {
        try {
          // atob expects base64, so decodeURIComponent first, then atob, then parse
          const decoded = atob(decodeURIComponent(tokenParam))
          payload = JSON.parse(decoded)
        } catch (e) {
          console.error('Failed to decode or parse token param', e)
          payload = {}
        }
      }

      const orgId = payload.org_id
      const pluginId = payload.plugin_id
      const accessToken = payload.access_token
      const refreshToken = payload.refresh_token
      const expiresIn = payload.expires_in

      if (!orgId || !pluginId || !accessToken) {
        console.error('Missing required OAuth parameters')
        // Send error message through channel
        channel.postMessage({
          status: 'error',
          message: 'Missing required OAuth parameters',
          orgId,
        })
        channel.close()
        window.close()
        return
      }

      try {
        // Fetch user info from Google
        const { name, email } = await fetchGoogleUserInfo(accessToken)
        // Send success message through channel with user info
        channel.postMessage({
          status: 'success',
          message: 'Plugin credentials successfully saved',
          orgId,
          payload: {
            access_token: accessToken,
            expires_in: expiresIn,
            org_id: orgId,
            plugin_id: pluginId,
            refresh_token: refreshToken,
            label: name, // Add user's name as label
            email: email, // Add user's email
          },
        })
        channel.close()
        window.close()
      } catch (error) {
        console.error('Error in OAuth callback:', error)
        // Send error message through channel
        channel.postMessage({
          status: 'error',
          message: 'Error saving plugin credentials',
          orgId,
          error: error instanceof Error ? error.message : String(error),
        })
        channel.close()
        window.close()
      }
    }

    handleCallback()
  }, [searchParams])

  return <Loading fullHeight />
}
